import { useState, useEffect } from "react";
import { Button, Input, Select, Form, Modal, Checkbox } from "antd";
import { useAppSelector } from "../../redux/hooks";

const { Option } = Select;

const OrderItemForm = (props: any) => {
  const [form] = Form.useForm();
  const {
    isOpen,
    handleCancel,
    onAddOrderItem,
    onUpdateOrderItem,
    selectedOrderItemData,
    assignment,
    orderItemTypes,
    title,
    isEditMode,
    isUnpaid,
    enableAdditionalCharges
  } = props;

  type Option = {
    id: number;
    name: string;
    position: number;
  };

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [quantity, setQuantity] = useState(selectedOrderItemData?.quantity);
  const [additionalCharges, setAdditionalCharges] = useState(
    selectedOrderItemData?.additional_charges || 0
  );
  const [rate, setRate] = useState(selectedOrderItemData?.rate);
  const [totalItemCost, setTotalItemCost] = useState(selectedOrderItemData?.total_item_cost);
  const [tax, setTax] = useState(selectedOrderItemData?.tax);
  const [unpaidWork, setUnpaidWork] = useState(isUnpaid);
  const [isRateOverridable, setIsRateOverridable] = useState(
    selectedOrderItemData?.order_item_type?.is_rate_overridable
  );
  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis", "super_user", "zonal_manager", "finance_controller"].includes(role.role_type)
  );

  useEffect(() => {
    const unpaidOrderItemTypes = orderItemTypes.filter(
      (orderItemType: any) => orderItemType.unpaid_work === true
    );
    const paidOrderItemTypes = orderItemTypes.filter(
      (orderItemType: any) => orderItemType.unpaid_work === false
    );
    if (isUnpaid && !isEditMode) {
      const sortedUnpaidOrderItemTypes = [...unpaidOrderItemTypes].sort(
        (a: any, b: any) => a.position - b.position
      );
      setOptions(sortedUnpaidOrderItemTypes);
    } else if (isEditMode) {
      const sortedOrderItemTypes = [...orderItemTypes].sort(
        (a: any, b: any) => a.position - b.position
      );
      setOptions(sortedOrderItemTypes);
    } else {
      const sortedpaidOrderItemTypes = [...paidOrderItemTypes].sort(
        (a: any, b: any) => a.position - b.position
      );
      setOptions(sortedpaidOrderItemTypes);
    }
  }, [orderItemTypes]);

  const onFinish = (values: any) => {
    setLoading(true);
    if (selectedOrderItemData?.id) {
      values["id"] = selectedOrderItemData?.id;
      const data = {
        id: assignment?.id,
        order_item: values
      };
      onUpdateOrderItem(data, setLoading, form);
    } else {
      const data = { id: assignment?.id, order_item: values };
      onAddOrderItem(data, setLoading, form);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    if (isEditMode === true) {
      form.setFieldsValue({ ...selectedOrderItemData });
    }
  }, [selectedOrderItemData]);

  useEffect(() => {
    if (isEditMode === true) {
      form.setFieldsValue({ ...selectedOrderItemData });
    }
  }, [selectedOrderItemData]);

  useEffect(() => {
    const total = (Number(rate) * Number(quantity) + Number(additionalCharges)).toFixed(2);
    form.setFieldValue("total_item_cost", total);
    setTotalItemCost(total);
  }, [rate, quantity, additionalCharges]);

  useEffect(() => {
    form.setFieldValue(
      "final_amount",
      (Number(totalItemCost) * (1 + Number(tax) / 100)).toFixed(2)
    );
  }, [totalItemCost, tax]);

  const handleOrderItemChange = (value: string) => {
    const orderItemType = orderItemTypes.find((obj: any) => obj.id === value);
    if (orderItemType) {
      setIsRateOverridable(orderItemType.is_rate_overridable);
      setQuantity(orderItemType.quantity);
      setRate(orderItemType.rate);
      setTax(orderItemType.tax_rate);
      form.setFieldsValue({
        ...orderItemType,
        tax: orderItemType.tax_rate,
        additional_charges: 0
      });
    }
  };

  useEffect(() => {
    if (isEditMode) {
      const orderItemTypeID = form.getFieldValue("order_item_type_id");
      const unpaid = orderItemTypes.filter(
        (orderItemType: any) => orderItemType.id === orderItemTypeID
      )[0]?.unpaid_work;
      setUnpaidWork(!!unpaid);
    }
  }, []);

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={handleCancel}
      onOk={onFinish}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          form="order_item_form"
          key="submit"
          loading={loading}
          htmlType="submit"
          type="primary"
        >
          {isEditMode ? "Update" : "Create"}
        </Button>
      ]}
    >
      <Form
        name="order_item_form"
        form={form}
        initialValues={selectedOrderItemData}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Form.Item
          name="order_item_type_id"
          noStyle
          rules={[{ required: true, message: "Please select order item type" }]}
        >
          <Select
            placeholder="Select order item type"
            allowClear
            showSearch
            optionFilterProp="children"
            onSelect={handleOrderItemChange}
            filterOption={(input, option: any) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {options?.map((orderItemType: any) => (
              <Option key={orderItemType?.name} value={orderItemType?.id}>
                {orderItemType?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="quantity" label="Quantity">
          <Input type="number" onChange={(e: any) => setQuantity(e.target.value)} />
        </Form.Item>

        {!unpaidWork && (
          <>
            <Form.Item name="rate" label="Rate">
              <Input
                type="number"
                disabled={!isRateOverridable}
                onChange={(e: any) => setRate(e.target.value)}
              />
            </Form.Item>

            <Form.Item name="additional_charges" label="Additional charges">
              <Input
                type="number"
                disabled={!enableAdditionalCharges}
                onChange={(e: any) => setAdditionalCharges(e.target.value)}
              />
            </Form.Item>

            <Form.Item name="total_item_cost" label="Total item cost">
              <Input
                type="number"
                disabled
                onChange={(e: any) => setTotalItemCost(e.target.value)}
              />
            </Form.Item>

            <Form.Item name="tax" label="Tax">
              <Input type="number" disabled />
            </Form.Item>

            <Form.Item name="final_amount" label="Final amount">
              <Input type="number" disabled />
            </Form.Item>
          </>
        )}

        <Form.Item name="actual_quantity_used" label="Actual quantity used">
          <Input type="number" />
        </Form.Item>

        {isEditUser && (
          <Form.Item name="amount_for_installer" label="Amount for installer">
            <Input
              type="number"
              onFocus={(e) =>
                e.target.addEventListener(
                  "wheel",
                  function (e) {
                    e.preventDefault();
                  },
                  { passive: false }
                )
              }
            />
          </Form.Item>
        )}

        <Form.Item label="Description" name="description">
          <Input.TextArea />
        </Form.Item>

        <Form.Item label="Customer accepted" valuePropName="checked" name="customer_accepted">
          <Checkbox>Tick if customer accepted</Checkbox>
        </Form.Item>
        {!unpaidWork && (
          <Form.Item label="Customer paid" valuePropName="checked" name="customer_paid">
            <Checkbox>Tick if customer paid</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default OrderItemForm;
